/**
 * Platform exams — the published datesheets (CBSE/ICSE boards, JEE, NEET).
 *
 * These belong to no class: they apply to a COHORT, so nobody has to re-enter a
 * national timetable per class and a student with no class still has real dates.
 * Admin-authored only; the class routes refuse to touch them.
 *
 * See docs/FeatureDesigns/goals-syllabus-revision-plan/one-preparation-model-target.md §2.1.
 */
import { authApi } from './auth';
import { API_BASE, fetchWithAdmin } from './fetchHelper';

export interface ExamPaper {
  id: string;
  subjects: string[];
  label?: string;
  date: string;
  time?: string;
  durationMinutes?: number;
}

export interface ExamCohort {
  board: string;
  /** Present for a board (class 10 and 12 sit different papers); absent for a track. */
  grade?: string;
  year: number;
}

export interface PlatformExam {
  id: string;
  ownerId: string;
  ownerScope: 'platform';
  name: string;
  type: 'unit-test' | 'class-test' | 'pre-board' | 'board' | 'mock';
  papers: ExamPaper[];
  cohort?: ExamCohort;
  /** `projected` = our estimate from the recurring window; `official` = announced. */
  status?: 'projected' | 'official';
  updatedAt?: string;
  updatedBy?: string;
}

async function write<T>(method: 'PUT' | 'DELETE', path: string, body?: unknown): Promise<T> {
  const token = authApi.getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
  return res.json() as Promise<T>;
}

export const platformExamApi = {
  listPlatformExams: () =>
    fetchWithAdmin<{ exams: PlatformExam[] }>('/console/platform-exams'),

  savePlatformExam: (exam: {
    cohort: ExamCohort; name: string; type: PlatformExam['type'];
    status: 'projected' | 'official'; papers: ExamPaper[];
  }) => write<{ exam: PlatformExam }>('PUT', '/console/platform-exams', exam),

  deletePlatformExam: (ownerId: string, examId: string) =>
    write<{ deleted: boolean }>(
      'DELETE',
      `/console/platform-exams/${encodeURIComponent(ownerId)}/${encodeURIComponent(examId)}`,
    ),
};
